import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "panel", "option", "liveRegion"]
  static values = {
    url: String,
    currentResource: String
  }

  connect() {
    this.debouncedSearch = this.debounce(this.performSearch.bind(this), 300)
    this.hideResultsOnClickOutside = this.hideResultsOnClickOutside.bind(this)
    this.currentHighlightedIndex = -1
    document.addEventListener('click', this.hideResultsOnClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.hideResultsOnClickOutside)
  }

  // Gestion de la saisie dans le champ de recherche
  input() {
    const query = this.inputTarget.value.trim()

    if (query.length >= 2) {
      this.debouncedSearch(query)
    } else {
      this.hideResults()
    }
  }

  // Navigation clavier dans les résultats
  handleKeydown(event) {
    if (!this.hasOptionTarget) return

    const options = this.optionTargets

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.highlightNextOption(options)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.highlightPreviousOption(options)
        break
      case 'Enter':
        event.preventDefault()
        this.selectHighlightedOption(options)
        break
      case 'Escape':
        event.preventDefault()
        this.hideResults()
        break
      case 'Home':
        event.preventDefault()
        this.highlightFirstOption(options)
        break
      case 'End':
        event.preventDefault()
        this.highlightLastOption(options)
        break
    }
  }

  highlightNextOption(options) {
    this.currentHighlightedIndex = Math.min(
      this.currentHighlightedIndex + 1,
      options.length - 1
    )
    this.updateHighlight(options)
  }

  highlightPreviousOption(options) {
    this.currentHighlightedIndex = Math.max(
      this.currentHighlightedIndex - 1,
      0
    )
    this.updateHighlight(options)
  }

  highlightFirstOption(options) {
    this.currentHighlightedIndex = 0
    this.updateHighlight(options)
  }

  highlightLastOption(options) {
    this.currentHighlightedIndex = options.length - 1
    this.updateHighlight(options)
  }

  updateHighlight(options) {
    // Retirer la mise en évidence de toutes les options
    options.forEach((option, index) => {
      if (index === this.currentHighlightedIndex) {
        option.setAttribute('aria-selected', 'true')
        option.classList.add('bg-blue-50')

        // Utiliser l'ID de l'option pour aria-activedescendant
        if (option.id) {
          this.inputTarget.setAttribute('aria-activedescendant', option.id)
        }

        // Assurer que l'option est visible (scroll si nécessaire)
        option.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else {
        option.setAttribute('aria-selected', 'false')
        option.classList.remove('bg-blue-50')
      }
    })
  }

  selectHighlightedOption(options) {
    if (this.currentHighlightedIndex >= 0 && this.currentHighlightedIndex < options.length) {
      const selectedOption = options[this.currentHighlightedIndex]
      selectedOption.click()
    }
  }

  // Effectuer la recherche avec Turbo Streams
  async performSearch(query) {
    try {
      const params = new URLSearchParams({
        q: query,
        current_resource: this.currentResourceValue || ''
      })

      // Utiliser Turbo Streams pour mettre à jour les résultats
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.showResults()

        // Attendre que le DOM soit mis à jour puis annoncer les résultats
        setTimeout(() => this.announceResults(), 100)
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error)
      this.announceLiveRegion('Erreur lors de la recherche')
    }
  }

  // Afficher les résultats
  showResults() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove('hidden')
      this.inputTarget.setAttribute('aria-expanded', 'true')
      this.currentHighlightedIndex = -1
    }
  }

  // Masquer les résultats
  hideResults() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add('hidden')
      this.inputTarget.setAttribute('aria-expanded', 'false')
      this.inputTarget.removeAttribute('aria-activedescendant')
      this.currentHighlightedIndex = -1
    }
  }

  // Masquer les résultats en cliquant à l'extérieur
  hideResultsOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  // Annoncer le nombre de résultats aux lecteurs d'écran
  announceResults() {
    const resultsContainer = document.querySelector('#search-results')
    if (!resultsContainer) return

    const resultCountElement = resultsContainer.querySelector('[data-result-count]')
    if (!resultCountElement) return

    const count = parseInt(resultCountElement.dataset.resultCount || '0')

    let message = ''
    if (count === 0) {
      message = 'Aucun résultat trouvé'
    } else if (count === 1) {
      message = '1 résultat trouvé'
    } else {
      message = `${count} résultats trouvés`
    }

    this.announceLiveRegion(message)
  }

  // Annoncer un message dans la live region
  announceLiveRegion(message) {
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = message

      // Clear le message après 1 seconde pour permettre de nouvelles annonces
      setTimeout(() => {
        this.liveRegionTarget.textContent = ''
      }, 1000)
    }
  }

  // Debounce function
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
} 